/**
 * Sync problems (phone commands the server did not apply): what each error code means for the
 * office, in plain words, and what to do about it.
 */

export interface SyncProblemExplanation {
  /** One line: what happened. */
  title: string
  /** What to do. */
  action: string
}

const BY_CODE: Record<string, SyncProblemExplanation> = {
  PARENT_MISSING: {
    title: 'Waiting for data from another phone',
    action:
      'This entry needs something made earlier (often on another phone of the same team) that has not reached the server. Make sure that phone opens the app with internet and syncs. It clears by itself once the data arrives.',
  },
  SESSION_CODE_TAKEN: {
    title: 'Collection day code already used',
    action:
      'The phone opened a collection day with a code the server already has. Usually the same day was opened twice. Check Collection days for that code; if the phone’s day is a duplicate, dismiss this and ask the supervisor to set it aside on the phone.',
  },
  UNKNOWN_VILLAGE: {
    title: 'Village not known to the server',
    action:
      'The phone works for a village the server does not have (deleted or never set up). Check the phone under Devices and the village under Locations, then ask the phone to retry.',
  },
  NO_VILLAGE: {
    title: 'Phone is not a village phone',
    action:
      'A collection-day entry came from a phone registered to a store or site. Check the phone under Devices.',
  },
  ID_ALREADY_USED: {
    title: 'Record id already used',
    action:
      'The phone tried to create a record with an id the server already uses for something else. This is usually a phone that was restored from a copy. Dismiss once checked and set it aside on the phone.',
  },
  SHIPMENT_CODE_TAKEN: {
    title: 'Trip code already used',
    action:
      'The phone made a trip (shipment) with a code the server already has. Check Shipments for that code; if it is the same trip, dismiss this and set it aside on the phone.',
  },
  UNKNOWN_ACTOR: {
    title: 'Person not known to the server',
    action:
      'The entry was made by a person the server does not have (removed user). Check the phone team, then ask the phone to retry or set it aside.',
  },
  UNKNOWN_TYPE: {
    title: 'App version not understood by the server',
    action:
      'The phone sent a kind of entry this server does not handle. The app on the phone is probably newer or older than the server. Update the app, then retry.',
  },
  INVALID_PAYLOAD: {
    title: 'Entry has missing or wrong details',
    action:
      'The server could not read the entry. Check the message below, then retry or set it aside on the phone.',
  },
  INVALID_COMMAND: {
    title: 'Entry could not be read',
    action:
      'The phone sent something the server could not understand. Update the app on the phone.',
  },
  SERVER_ERROR: {
    title: 'Server failed while saving',
    action:
      'Nothing was saved; the phone keeps trying by itself. If it keeps failing, send the message below to the engineering team.',
  },
}

const BY_RESULT: Record<string, SyncProblemExplanation> = {
  WAIT_FOR_PARENT: BY_CODE.PARENT_MISSING!,
  ERROR: BY_CODE.SERVER_ERROR!,
  REJECTED_MALFORMED: {
    title: 'Refused by the server',
    action:
      'The server will not accept this entry as it is. Read the message below, fix the data if needed, and ask the phone to retry — or dismiss it here and set it aside on the phone.',
  },
}

/** The office explanation for a problem: by its error code, else by its result. */
export function explainSyncProblem(p: {
  errorCode: string | null
  result: string
}): SyncProblemExplanation {
  return (
    (p.errorCode ? BY_CODE[p.errorCode] : undefined) ??
    (p.errorCode?.startsWith('HTTP_') ? BY_CODE.SERVER_ERROR : undefined) ??
    BY_RESULT[p.result] ?? {
      title: 'Not applied',
      action: 'Read the message below.',
    }
  )
}

export const SYNC_RESULT_LABEL: Record<string, string> = {
  REJECTED_MALFORMED: 'Refused',
  WAIT_FOR_PARENT: 'Waiting',
  ERROR: 'Server error',
}
